import { Injectable } from '@angular/core';
import { FeatureFlag } from '@core/enums';
import { User } from '@core/models';
import { AccountService } from '@core/services';
import { Action, createSelector, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { append, patch, removeItem } from '@ngxs/store/operators';
import { RelatedToType } from '@shared/enums';
import { Approval } from '@shared/modules/approvals/approvals.types';
import { defaultTaskTableFields } from '@tasks/tasks.constants';
import { ensureSortFieldsInSelect } from '@tasks/sort-fields-sync.utils';
import { DatadogService } from '@shared/services/datadog.service';
import UrlUtils from '@shared/Utils/url.utils';
import { AccountState } from '@state/account/state';
import { SavedViewsActions } from '@state/saved-view/action';
import { SavedViewsState } from '@state/saved-view/state';
import { TaskListTemplateService } from '@tasks/services/task-list-template.service';
import { TaskListService } from '@tasks/services/task-list.service';
import { TaskTagsService } from '@tasks/services/task-tags.service';
import { TasksBaseUrlService } from '@tasks/services/tasks-base-url.service';
import { PretendTaskChangePusher } from '@tasks/services/pretend-task-change-pusher.service';
import { TasksService } from '@tasks/services/tasks.service';
import {
  DurationType,
  LATEST_CONFETTI_LAUNCH_DATE_KEY,
  Task,
  TaskType,
  DraftTaskChange,
  TaskList,
  TaskListTemplate,
  TaskQueryFilter,
  TaskRelatedToModule,
  TaskTag,
  TaskView,
  TaskViewType,
  Todo,
  InfiniteScrollData,
} from '@tasks/tasks.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import confetti from 'canvas-confetti';
import { cloneDeep, noop } from 'lodash';
import moment from 'moment';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, expand, filter as rxFilter, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TsSnackbarService } from 'tsui';

import { TaskUtils } from '../tasks.utils';
import { TaskActions } from './actions';
import { computeTaskUpdatePatch } from './task-state.reducers';
import { ContactsService } from '@modules/contacts/contacts.service';

/**
 * Workaround for grouped infinite-scroll flicker: bootstrap with a large initial page
 * (300), then auto-fetch +100 immediately, then 100 per scroll trigger.
 *
 * The initial 300 is recorded as `currentPage = INFINITE_SCROLL_INITIAL_PAGES` (3) of
 * `pageSize = INFINITE_SCROLL_PAGE_SIZE` (100) so that `currentPage * pageSize` continues
 * to equal the actual loaded window — relied on by SyncCascadingChanges, redispatchTaskFetch,
 * and FetchPartialTaskData.
 */
const INFINITE_SCROLL_INITIAL_PAGE_SIZE = 300;
const INFINITE_SCROLL_PAGE_SIZE = 100;
const INFINITE_SCROLL_INITIAL_PAGES = INFINITE_SCROLL_INITIAL_PAGE_SIZE / INFINITE_SCROLL_PAGE_SIZE;

/** Fields required for the drawer pre-fetch zone (Instant Preview). Always included in FilterTasks/GetTasks select. */
const PRE_FETCH_SELECT_FIELDS: string[] = [
  'approvals',
  'approver_names',
  'assigned_user_id',
  'assigned_user',
  'contact_ids',
  'contact_names',
  'contacts',
  'description',
  'due_date',
  'duration',
  'duration_type',
  'following_user_names',
  'followings',
  'lag',
  'linked_to',
  'predecessors',
  'predecessor_type',
  'predecessor_task_id',
  'predecessor_task_ids',
  'start_date',
  'title',
  'task_section_titles',
  'workflow_state',
  'name',
];

export interface TaskStateModel {
  apiBaseUrl: string;
  relatedToType: TaskRelatedToModule;
  relatedToTypeId: number;
  customObjectName?: string;
  viewType: TaskViewType;
  tasks: Task[];
  /**
   * [SC-57718] Predecessor "task picker" options for the currently-open task.
   * This list is intentionally NOT the same as `tasks` (which is paginated).
   */
  taskPredecessorOptions?: Task[];
  /**
   * When true, the next `LoadTaskPredecessorOptions` must hit the API. Set by `InvalidatePredecessorOptions`
   * (title updates, create/delete/duplicate, bulk ops, task-view switch, module context reset).
   */
  predecessorOptionsDirty?: boolean;
  queryFilters?: TaskQueryFilter;
  selectedTasks?: Partial<Task>[];
  taskView: TaskView;
  previousTaskViewVersion?: TaskView;
  taskLists: TaskList[];
  taskTemplateId?: number;
  taskListTemplate?: TaskListTemplate;
  sidebarItems: TaskView[];
  currentTask: Task;
  taskTags: TaskTag[];
  taskContacts: Contact[];
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
  };
  infiniteScroll?: InfiniteScrollData;
  taskCounters: {
    [key: number]: { currentTaskCount: number; currentTaskCompleteCount: number };
  };
  draftPreviewChanges: DraftTaskChange[];
  draftPublishPusherChannel: string | null;
}

@State<TaskStateModel>({
  name: new StateToken<TaskStateModel>('taskState'),
  defaults: {
    relatedToType: null,
    relatedToTypeId: null,
    viewType: TaskViewType.List,
    tasks: [],
    taskPredecessorOptions: [],
    predecessorOptionsDirty: true,
    selectedTasks: [],
    queryFilters: null,
    taskView: null,
    previousTaskViewVersion: null,
    taskLists: [],
    sidebarItems: [],
    taskTemplateId: null,
    taskListTemplate: null,
    apiBaseUrl: '',
    currentTask: null,
    taskTags: [],
    taskContacts: [],
    pagination: undefined,
    infiniteScroll: undefined,
    taskCounters: {},
    draftPreviewChanges: [],
    draftPublishPusherChannel: null,
  },
})
@Injectable()
export class TaskState {
  private readonly cancelGetTaskDetailOnUpdate$ = new Subject<number>();

  constructor(
    private taskBaseUrlService: TasksBaseUrlService,
    private taskService: TasksService,
    private taskListsService: TaskListService,
    private taskListTemplateService: TaskListTemplateService,
    private taskTagsService: TaskTagsService,
    private accountService: AccountService,
    private contactsService: ContactsService,
    private dataDogService: DatadogService,
    private store: Store,
    private snackbar: TsSnackbarService,
    private pretendTaskChangePusher: PretendTaskChangePusher,
  ) {}

  get isAggregatedTasks() {
    return this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType === TaskRelatedToModule.AggregatedTasks;
  }

  get isTaskListTemplate() {
    return (
      (this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType as string) ===
      TaskRelatedToModule.TaskListTemplate
    );
  }

  get isInfiniteScrollMode(): boolean {
    const flagOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.INFINITE_SCROLL_TASKS));
    const view = this.store.selectSnapshot(TaskState.getViewType);
    return flagOn && [TaskViewType.List, TaskViewType.Gantt].includes(view);
  }

  @Selector()
  static getTaskCounters(state: TaskStateModel): {
    [key: number]: { currentTaskCount: number; currentTaskCompleteCount: number };
  } {
    return state.taskCounters;
  }

  @Selector()
  static getDraftPreviewChanges(state: TaskStateModel): DraftTaskChange[] {
    return state.draftPreviewChanges ?? [];
  }

  @Selector()
  static getDraftPublishPusherChannel(state: TaskStateModel): string | null {
    return state.draftPublishPusherChannel;
  }

  @Action(TaskActions.SetRelatedTo)
  setRelatedToType(
    context: StateContext<TaskStateModel>,
    { relatedToType, relatedToTypeId, customObjectName }: TaskStateModel,
  ) {
    const apiBaseUrl = this.taskBaseUrlService.getTasksUrl(relatedToType, relatedToTypeId);
    context.patchState({ apiBaseUrl });

    if (relatedToType !== context.getState().relatedToType || relatedToTypeId !== context.getState().relatedToTypeId) {
      context.patchState({
        tasks: [],
        taskView: null,
        previousTaskViewVersion: null,
        currentTask: null,
        selectedTasks: [],
        taskLists: [],
        sidebarItems: [],
        relatedToType,
        relatedToTypeId,
        taskListTemplate: null,
        taskTemplateId: relatedToType === 'TaskListTemplate' ? relatedToTypeId : null,
        apiBaseUrl,
        customObjectName,
        taskCounters: {},
        taskPredecessorOptions: [],
        predecessorOptionsDirty: true,
        infiniteScroll: undefined,
      });
    }
  }

  @Action(TaskActions.SetViewType)
  setViewType(context: StateContext<TaskStateModel>, { viewType }: { viewType: TaskViewType }) {
    context.patchState({ viewType });
  }

  @Action(TaskActions.GetTaskTags)
  getTaskTags(context: StateContext<TaskStateModel>) {
    return this.taskTagsService.getTaskTags().pipe(
      tap((taskTags) => {
        context.patchState({ taskTags });
      }),
    );
  }

  @Selector()
  static getTaskTags(state: TaskStateModel): TaskTag[] {
    return state.taskTags || [];
  }

  @Action(TaskActions.GetTaskContacts)
  getTaskContacts(context: StateContext<TaskStateModel>) {
    return this.contactsService.getContacts(1, 1000, 'name', 'asc', '', {}, false, true, ['id', 'name']).pipe(
      tap((response) => {
        context.patchState({ taskContacts: response.contacts });
      }),
    );
  }

  @Selector()
  static getTaskContacts(state: TaskStateModel): Contact[] {
    return state.taskContacts || [];
  }

  @Selector()
  static getTaskRelatedTo(state: TaskStateModel): {
    relatedToType: TaskRelatedToModule;
    relatedToTypeId: number;
    customObjectName?: string;
  } {
    return cloneDeep({
      relatedToType: state.relatedToType,
      relatedToTypeId: state.relatedToTypeId,
      customObjectName: state.customObjectName,
    });
  }

  @Selector()
  static getApiBaseUrl(state: TaskStateModel): string {
    return state.apiBaseUrl;
  }

  @Selector()
  static getViewType(state: TaskStateModel): TaskViewType {
    return state.viewType;
  }

  @Selector()
  static getPagination(state: TaskStateModel): { page: number; pageSize: number; totalItems: number } {
    return state?.pagination;
  }

  @Selector()
  static getInfiniteScroll(state: TaskStateModel): InfiniteScrollData | undefined {
    return state?.infiniteScroll;
  }

  @Selector()
  static getQueryFilters(state: TaskStateModel): TaskQueryFilter {
    return state.queryFilters;
  }

  @Selector()
  static getTaskPredecessorOptions(state: TaskStateModel): Task[] {
    return state.taskPredecessorOptions || [];
  }

  static getTaskById(taskId: number) {
    return createSelector([TaskState], (state: TaskStateModel) => TaskUtils.deepFindTaskById(state.tasks, taskId));
  }

  // =========================
  // TASK

  @Selector()
  static getCurrentTask(state: TaskStateModel): Task {
    return state?.currentTask;
  }

  @Action(TaskActions.SetPage)
  setCurrentPage(context: StateContext<TaskStateModel>, { page }: { page: number }) {
    context.patchState({ pagination: { ...context.getState().pagination, page } });
  }
  @Action(TaskActions.SetPageSize)
  setCurrentPageSize(context: StateContext<TaskStateModel>, { pageSize }: { pageSize: number }) {
    context.patchState({ pagination: { ...context.getState().pagination, pageSize } });
  }

  @Action(TaskActions.GetTaskDetail, { cancelUncompleted: true })
  getTaskById(context: StateContext<TaskStateModel>, { taskListId, taskId }: { taskListId: number; taskId: number }) {
    // TODO MJR TsRxjsOperators.handle404toNotFound(this.router),
    return this.taskService.getTaskDetailById(taskListId, taskId).pipe(
      takeUntil(this.cancelGetTaskDetailOnUpdate$.pipe(rxFilter((id) => id === taskId))),
      tap((task) => {
        task.children = TaskUtils.deepSortTasksByRank(task.children);
        task.todos = task.todos?.sort((a, b) => a.rank - b.rank);
        context.patchState({ currentTask: task });
      }),
    );
  }

  @Action(TaskActions.DuplicateTask)
  duplicateTask(
    context: StateContext<TaskStateModel>,
    { taskListId, task, templateId }: { taskListId: number; task: Task; templateId: number },
  ) {
    return this.taskService.duplicateTask(taskListId, templateId, task).pipe(
      tap(() => context.dispatch(new TaskActions.InvalidatePredecessorOptions())),
      switchMap(() => this.redispatchTaskFetch(context)),
      this.updateCount(context),
    );
  }

  @Action(TaskActions.ClearCurrentTask)
  clearCurrentTask(context: StateContext<TaskStateModel>) {
    context.patchState({ currentTask: null });
  }

  @Action(TaskActions.SetCurrentTaskPreview)
  setCurrentTaskPreview(context: StateContext<TaskStateModel>, { task }: TaskActions.SetCurrentTaskPreview) {
    context.patchState({ currentTask: task as Task });
  }

  @Action(TaskActions.CreateTask)
  createTask(
    context: StateContext<TaskStateModel>,
    {
      model,
    }: {
      model: Partial<Task>;
    },
  ) {
    let currentTasks = cloneDeep(context.getState().tasks);
    const currentTaskView = cloneDeep(context.getState().taskView);
    const task: Task = {
      ...model,
      task_type: model.task_type,
      task_list_id: currentTaskView.task_list_id || currentTaskView.id,
      rank: model.rank || 0,
      parent_id: model.parent_id,
      linked_key_date_field_as_source: model.linked_key_date_field_as_source,
      linked_key_date_field_as_target: model.linked_key_date_field_as_target,
    };

    const defaultDurationType = (currentTaskView as any)?.default_dates_behavior as DurationType;
    (task as any).duration_type = (task as any).duration_type || defaultDurationType || DurationType.BusinessDays;

    return this.taskService.createTask(task.task_list_id, task).pipe(
      catchError((error) => {
        const objectType = task.task_type === 'key_date' ? 'Key Date' : 'Task';
        const errorMessage =
          error?.error?.error || `Unable to create a new ${objectType} at this time. Please try again later.`;

        this.snackbar.open(errorMessage, 'error', null, {
          duration: 5000,
        });
        return of(null);
      }),
      tap((newTask: Task) => {
        if (!newTask) {
          return;
        }

        if (task.group_path) {
          newTask['group_path'] = [...task.group_path, newTask.id];
          newTask['group_path_key'] = String(newTask.id);
        }

        if (newTask.parent_id) {
          const parentTask = TaskUtils.deepFindTaskById(currentTasks, newTask.parent_id);
          parentTask.children = [...parentTask.children, newTask];
          currentTasks = TaskUtils.deepUpdateTaskById(currentTasks, parentTask);
        } else {
          currentTasks.push(newTask);
        }

        const newState = {
          tasks: currentTasks,
          currentTask: newTask,
        };
        context.patchState(newState);
        context.dispatch(new TaskActions.InvalidatePredecessorOptions());
      }),
      this.updateCount(context),
    );
  }

  @Action(TaskActions.CreateSubTask)
  createSubTask(context: StateContext<TaskStateModel>) {
    const nextCurrentTaskRank = () => {
      const currentTask = context.getState().currentTask;
      if (!currentTask.children || currentTask.children.length === 0) {
        return 1;
      }

      return Math.max(...currentTask.children.map((child) => child.rank)) + 1;
    };

    const currentTask = { ...context.getState().currentTask };

    let subtask = {
      task_type: currentTask.task_type,
      task_list_id: currentTask.task_list_id,
      rank: nextCurrentTaskRank(),
      parent_id: currentTask.id,
    };

    const currentTaskView = context.getState().taskView;
    const defaultDurationType = (currentTaskView as any)?.default_dates_behavior as DurationType;
    (subtask as any).duration_type = (subtask as any).duration_type || defaultDurationType || DurationType.BusinessDays;

    return this.taskService.createTask(currentTask.task_list_id, subtask).pipe(
      tap((task) => {
        currentTask.children = [...(currentTask.children ?? []), task];
        const currentTaskGroupPath = context.getState().tasks.find((t) => t.id === currentTask.id)?.group_path ?? [];
        task['group_path'] = [...currentTaskGroupPath, task.id];
        task['group_path_key'] = String(task.id);
        // Update parent flags in state.tasks
        const tasks = context.getState().tasks.map((t) =>
          t.id === currentTask.id
            ? { ...t, has_children: true, num_children: (t.num_children ?? 0) + 1 }
            : t,
        );
        tasks.push(task);
        context.patchState({ currentTask, tasks });
        context.dispatch(new TaskActions.InvalidatePredecessorOptions());
      }),
      catchError((error) => {
        this.snackbar.open('Unable to create subtask. Please try again.', 'error');
        return of(null);
      }),
      this.updateCount(context),
    );
  }

  @Action(TaskActions.UpdateTask)
  updateTask(
    context: StateContext<TaskStateModel>,
    { task, templateId, recalculateTaskList }: { task: Task; templateId: number; recalculateTaskList: boolean },
  ) {
    const taskView = context.getState().taskView;
    if (taskView?.is_draft && task.task_list_id && task.task_list_id !== taskView.id) {
      this.snackbar.open('Unable to update. Task list mismatch.', 'error', undefined, {
        duration: 5000,
      });
      return EMPTY;
    }

    Object.keys(task).forEach((key) => {
      if (['start_date', 'due_date', 'completed_at', 'start', 'end_date', 'end'].includes(key)) {
        task[key] = task[key] ? moment(task[key]).format('YYYY-MM-DD') : null;
      }
    });

    const notAllowedAttributes = [
      'related_to_id',
      'related_to_type',
      'related_to_name',
      'task_section_id',
      'approver_id',
      'contact_id',
      'following_user_id',
    ];

    notAllowedAttributes.forEach((attribute) => {
      delete task[attribute];
    });

    if (task.id === context.getState().currentTask?.id) {
      this.cancelGetTaskDetailOnUpdate$.next(task.id);
    }

    const groupBy = context.getState().taskView?.definition?.table?.groupBy;
    const resyncStrategy = TaskUtils.classifyUpdateResync(task, groupBy);

    // if update title, we need to invalidate predecessor options
    const updatePayloadIncludesTitle = 'title' in task;

    return this.taskService.updateTaskById(task, templateId)?.pipe(
      catchError((error) => {
        this.snackbar.open(error.error.error, 'error', null, {
          duration: 5000,
        });
        return EMPTY;
      }),
      tap((updatedTask) => {
        if (!updatedTask) {
          console.error('Error updating task');
          return;
        }

        const state = context.getState();
        const prevTaskVersion = TaskUtils.deepFindTaskById(state.tasks, updatedTask.id);

        const patch = computeTaskUpdatePatch(state, updatedTask);
        context.patchState(patch);

        if (updatePayloadIncludesTitle) {
          context.dispatch(new TaskActions.InvalidatePredecessorOptions());
        }

        if (
          prevTaskVersion?.workflow_state !== updatedTask.workflow_state &&
          updatedTask.workflow_state === 'complete'
        ) {
          context.dispatch(new TaskActions.LaunchConfetti());
        }

        if (resyncStrategy.needsResync && context.getState().taskView) {
          const queryFilters = context.getState().queryFilters;
          if (resyncStrategy.groupByChanged) {
            this.redispatchTaskFetch(context);
          } else {
            context.dispatch(new TaskActions.SyncCascadingChanges(queryFilters));
          }
        }
      }),
      this.updateCount(context),
    );
  }

  @Action(TaskActions.LaunchConfetti)
  launchConfetti(context: StateContext<TaskStateModel>) {
    const latestConfettiLaunchDate = UrlUtils.getFromLocalStorage(LATEST_CONFETTI_LAUNCH_DATE_KEY);
    const currentDate = new Date();
    const lastConfettiLaunchDate = new Date(latestConfettiLaunchDate);
    const diffTime = moment(currentDate).diff(moment(lastConfettiLaunchDate), 'minutes');
    if (diffTime < 10) {
      return;
    }

    UrlUtils.saveToLocalStorage(LATEST_CONFETTI_LAUNCH_DATE_KEY, currentDate);

    confetti({
      spread: 360,
      ticks: 60,
      gravity: 0,
      decay: 0.96,
      startVelocity: 20,
      scalar: 1.5,
    });
    this.dataDogService.logAction('Tasks - Launch Confetti');
  }

  @Action(TaskActions.UnlinkTask)
  unlinktask(context: StateContext<TaskStateModel>, { task }: { task: Task }) {
    return this.taskService.unlinkTask(task.id).pipe(tap(() => {}));
  }

  @Action(TaskActions.CreateTodo)
  createTodo(context: StateContext<TaskStateModel>) {
    const currentTask = cloneDeep(context.getState().currentTask);

    let rank = 0;
    if (currentTask.todos?.length > 0) {
      rank = currentTask.todos.length - 1;
    }

    let todo = {
      title: '',
      done: false,
      editing: true,
      rank: rank,
    };

    currentTask.todos?.push(todo);

    return this.taskService.updateTaskById(currentTask, null).pipe(
      tap((task) => {
        task.children = TaskUtils.deepSortTasksByRank(task.children);
        const tasks = TaskUtils.deepUpdateTaskById(cloneDeep(context.getState().tasks), task);
        context.patchState({ currentTask: task, tasks });
      }),
    );
  }

  @Action(TaskActions.DeleteTodo)
  deleteTodo(context: StateContext<TaskStateModel>, { task, todo }: { task: Task; todo: Todo }) {
    task.todos = task.todos.map((t) => {
      if (t.id === todo.id) {
        todo['_destroy'] = true;
        return todo;
      }
      return t;
    });

    context.dispatch(new TaskActions.UpdateTask(task));
  }

  @Action(TaskActions.AddApprover)
  addApprover(context: StateContext<TaskStateModel>, { task, approval }: { task: Task; approval: Approval }) {
    const currentTask = cloneDeep(context.getState().currentTask);

    return this.taskService.addApproverToTask(task.task_list_id, task.id, approval).pipe(
      tap((approval) => {
        if (!task.approvals) {
          task.approvals = [];
        }

        task.approvals.push(approval);

        let newState;
        if (task.id === currentTask?.id) {
          newState = { currentTask: task };
        }

        newState = { ...newState, tasks: TaskUtils.deepUpdateTaskById(context.getState().tasks, task) };
        context.patchState(newState);
      }),
    );
  }

  @Action(TaskActions.RemoveApprover)
  removeApprover(context: StateContext<TaskStateModel>, { task, approval }: { task: Task; approval: Approval }) {
    const currentTask = cloneDeep(context.getState().currentTask);
    return this.taskService.removeApproverFromTask(task.task_list_id, task.id, approval).pipe(
      tap(() => {
        task.approvals = task.approvals.filter((a) => a.id !== approval.id);
        let newState;
        if (task.id === currentTask?.id) {
          newState = { currentTask: task };
        }

        newState = { ...newState, tasks: TaskUtils.deepUpdateTaskById(context.getState().tasks, task) };
        context.patchState(newState);
      }),
    );
  }

  @Action(TaskActions.AddFollower)
  addFollower(context: StateContext<TaskStateModel>, { task, user }: { task: Task; user: User }) {
    task.followings.unshift(user);

    const newTask = { ...task, following_user_ids: task.followings.map((user) => user.id) };
    context.dispatch(new TaskActions.UpdateTask(newTask));
  }

  @Action(TaskActions.RemoveFollower)
  removeFollower(context: StateContext<TaskStateModel>, { task, user }: { task: Task; user: User }) {
    task.followings.splice(
      task.followings.findIndex((item) => item.id === user.id),
      1,
    );

    const newTask = { ...task, following_user_ids: task.followings.map((user) => user.id) };
    context.dispatch(new TaskActions.UpdateTask(newTask));
  }

  @Action(TaskActions.AddContact)
  addContact(context: StateContext<TaskStateModel>, { task, contact }: { task: Task; contact: Contact }) {
    task.contacts.unshift(contact);

    const newTask = { ...task, contact_ids: task.contacts.map((contact) => contact.id) };
    context.dispatch(new TaskActions.UpdateTask(newTask));
  }

  @Action(TaskActions.RemoveContact)
  removeContact(context: StateContext<TaskStateModel>, { task, contact }: { task: Task; contact: Contact }) {
    task.contacts.splice(
      task.contacts.findIndex((item) => item.id === contact.id),
      1,
    );
    const newTask = { ...task, contact_ids: task.contacts.map((contact) => contact.id) };
    context.dispatch(new TaskActions.UpdateTask(newTask));
  }

  @Action(TaskActions.SetTaskView)
  setTaskView(context: StateContext<TaskStateModel>, { taskView }: { taskView: TaskView }) {
    if (!taskView) {
      return;
    }
    const updateProjectAndPropertyIdFieldsToTile = (fields) => {
      return fields.map((f) => {
        if (f === 'project_id') {
          f = f.replace('_id', '_title');
        }
        if (f === 'property_id') {
          f = f.replace('_id', '_name');
        }
        return f;
      });
    };

    if (taskView.is_draft) {
      taskView.type = RelatedToType.TaskList;
    }

    const state = { taskView };
    taskView.definition =
      typeof taskView?.definition === 'string' ? JSON.parse(taskView.definition || '{}') : taskView.definition;

    if (taskView.type === 'SavedView') {
      if (taskView.definition['fields']) {
        taskView.definition.table.fields = updateProjectAndPropertyIdFieldsToTile(taskView.definition['fields']);
      } else {
        taskView.definition['fields'] = updateProjectAndPropertyIdFieldsToTile(
          taskView.definition.table.fields || defaultTaskTableFields,
        );
      }

      if (!taskView.definition.filters) {
        taskView.definition.filters = taskView.saved_filter.filters;
      }
    }

    if (taskView?.id !== context.getState().previousTaskViewVersion?.id) {
      state['previousTaskViewVersion'] = taskView;
    }

    taskView.definition = ensureSortFieldsInSelect(taskView.definition);

    const previousViewId = context.getState().taskView?.id;
    context.patchState(state);
    if (previousViewId != null && taskView.id !== previousViewId) {
      context.dispatch(new TaskActions.InvalidatePredecessorOptions());
    }
  }

  @Action(TaskActions.UndoTaskViewChanges)
  undoTaskViewChanges(context: StateContext<TaskStateModel>) {
    const previousTaskViewVersion = context.getState().previousTaskViewVersion;
    if (previousTaskViewVersion) {
      context.patchState({ taskView: cloneDeep(previousTaskViewVersion) });
    }
  }

  @Selector()
  static getSidebarItems(state: TaskStateModel): any[] {
    //TODO MJR: Add type
    return state?.sidebarItems;
  }

  @Selector()
  static getVisibleSidebarItems(state: TaskStateModel): any[] {
    return state?.sidebarItems?.filter((x) => x.visibility !== 'private_visibility');
  }

  @Action(TaskActions.UpdateTaskView)
  updateTaskView(context: StateContext<TaskStateModel>, { taskView }: { taskView: TaskView }) {
    const currentTaskView = context.getState().taskView;
    if (taskView.type === 'TaskList' || taskView.type === 'TaskListTemplate') {
      context.patchState({ previousTaskViewVersion: taskView });
      return context.dispatch(new TaskActions.UpdateTaskList(taskView));
    } else if (taskView.type === 'SavedView') {
      // "convert" taskView to SavedView/SavedFilter
      const savedView = {
        ...taskView,
        definition: JSON.stringify({
          table: {
            ...(taskView.definition?.table ?? {}),
            fields: taskView.definition.fields,
            groupBy: taskView.definition?.table?.groupBy,
          },
        }),
        saved_filter: {
          ...taskView.saved_filter,
          filters: taskView.definition.filters,
        },
      };

      return this.store.dispatch(new SavedViewsActions.UpdateView(savedView)).pipe(
        switchMap(() => this.accountService.updateFilter(savedView.saved_filter)),
        tap(() => {
          if (savedView.id === currentTaskView.id) {
            context.patchState({ previousTaskViewVersion: savedView });
          }

          context.dispatch(new TaskActions.GetSidebarItems());
        }),
      );
    }
  }

  @Action(TaskActions.UpdateSectionTitle)
  updateSectionTitle(context: StateContext<TaskStateModel>, { task }: { task: Partial<TaskTag> }) {
    return this.taskTagsService.updateTaskTag(task).pipe(
      tap((taskTag) => {
        const currentTaskTags = context.getState().taskTags as TaskTag[];
        const updatedTaskTags = currentTaskTags.map((tag) => {
          if (tag.id === task.id) {
            return taskTag as TaskTag;
          }
          return tag;
        });
        context.patchState({ taskTags: updatedTaskTags });
        //dispatch tasks
        this.redispatchTaskFetch(context);
      }),
    );
  }

  @Action(TaskActions.GetSidebarItems)
  getSidebarItems(context: StateContext<TaskStateModel>) {
    // TODO MJR: Create a TaskSidebarService and move it to there.
    const relatedToType = context.getState().relatedToType;
    if (this.isAggregatedTasks) {
      return this.store.dispatch(new SavedViewsActions.LoadViews()).pipe(
        tap(() => {
          const savedviews = this.store.selectSnapshot(SavedViewsState.getViews);
          const sidebarItems = savedviews
            .filter((sv) => sv.related_to_type === RelatedToType.AggregatedTasks)
            .map((sv) => ({ ...sv, type: 'SavedView' }));
          context.patchState({ sidebarItems: sidebarItems as TaskView[], taskLists: sidebarItems as TaskView[] });
        }),
      );
    } else if (relatedToType === 'TaskListTemplate') {
      const relatedToId = context.getState().relatedToTypeId;
      return this.taskListTemplateService.getTaskListTemplateById(relatedToId).pipe(
        tap((taskListTemplate) => {
          const taskView = {
            ...taskListTemplate,
            ...taskListTemplate.task_list,
            type: 'TaskListTemplate',
            task_list_template_id: relatedToId,
          } as TaskView;
          context.patchState({
            sidebarItems: [taskView],
            taskLists: [taskView],
            taskCounters: {
              [taskListTemplate.task_list_id]: {
                currentTaskCount: taskListTemplate.task_list?.task_count ?? 0,
                currentTaskCompleteCount: taskListTemplate.task_list?.complete_task_count ?? 0,
              },
            },
          });
        }),
      );
    } else {
      return this.taskListsService.getTaskLists().pipe(
        tap((taskLists) => {
          context.patchState({
            sidebarItems: taskLists.map((tl) => ({ ...tl, type: 'TaskList' })),
            taskCounters: taskLists.reduce((p, c) => {
              return {
                ...p,
                [c.id]: { currentTaskCount: c.task_count, currentTaskCompleteCount: c.complete_task_count },
              };
            }, {}),
          });
        }),
      );
    }
  }

  @Action(TaskActions.SetSidebarItems)
  setSidebarItems(context: StateContext<TaskStateModel>, { sidebarItems }: TaskActions.SetSidebarItems) {
    context.patchState({ sidebarItems });
  }

  @Selector()
  static getSelectedTasks(state: TaskStateModel): Partial<Task>[] {
    return state?.selectedTasks;
  }

  @Action(TaskActions.SetSelectedTasks)
  setSelectedTasks(context: StateContext<TaskStateModel>, { tasks }: { tasks: Task[] }) {
    context.patchState({ selectedTasks: tasks });
  }

  @Action(TaskActions.BulkUpdateSelectedTasks)
  bulkUpdateSelectedTasks(context: StateContext<TaskStateModel>, { newTaskParams }: { newTaskParams: Partial<Task> }) {
    const selectedTasks = cloneDeep(context.getState().selectedTasks);

    const bulkParams = selectedTasks.map((task) => {
      return { id: task.id, ...newTaskParams };
    });

    return this.taskService.bulkUpdateTasks(bulkParams).pipe(
      tap((res) => {
        const currentTasks = cloneDeep(context.getState().tasks);
        const newTasks = res.reduce((acc, task) => {
          return TaskUtils.deepUpdateTaskById(acc, task);
        }, currentTasks);

        context.patchState({ tasks: newTasks });
      }),
      switchMap(() => this.redispatchTaskFetch(context)),
    );
  }

  @Action(TaskActions.BulkDeleteSelectedTasks)
  bulkDeleteSelectedTasks(context: StateContext<TaskStateModel>) {
    const selectedTasks = cloneDeep(context.getState().selectedTasks);
    const bulkParams = selectedTasks.map((task) => {
      return { id: task.id };
    });

    return this.taskService.bulkDeleteTasks(bulkParams).pipe(
      tap((res) => {
        const currentTasks = cloneDeep(context.getState().tasks);
        const deletedIds = selectedTasks.map((task) => +task.id);
        for (const task of selectedTasks) {
          const childGroupPathKeys = task.child_group_path_keys ?? [];
          for (const key of childGroupPathKeys) {
            const task = currentTasks.find((t) => t.group_path_key === key);
            if (task) {
              deletedIds.push(task.id);
            }
          }
        }

        const newTasks = TaskUtils.deepTaskDeleteById(currentTasks, deletedIds);
        let newState: Partial<TaskStateModel> = { tasks: newTasks };

        const currentTask = context.getState().currentTask;
        if (currentTask && deletedIds.includes(currentTask.id)) {
          newState['currentTask'] = null;
        }
        context.patchState(newState);
        context.dispatch(new TaskActions.InvalidatePredecessorOptions());
      }),
      this.updateCount(context),
    );
  }

  @Action(TaskActions.BulkDuplicateSelectedTasks)
  bulkDuplicateSelectedTasks(context: StateContext<TaskStateModel>) {
    const selectedTasks = cloneDeep(context.getState().selectedTasks);

    const bulkParams = selectedTasks.map((task) => {
      return { id: task.id };
    });
    const currentView = context.getState().taskView;
    return this.taskService.bulkDuplicateTasks(bulkParams).pipe(
      tap(() => context.dispatch(new TaskActions.InvalidatePredecessorOptions())),
      mergeMap(() => {
        const queryFilters = context.getState().queryFilters;
        if (queryFilters) {
          return this.redispatchTaskFetch(context);
        } else {
          return context.dispatch(new TaskActions.GetTasks(currentView.id, {}));
        }
      }),
      this.updateCount(context),
    );
  }

  // =========================
  // TASK LIST

  @Selector()
  static getTaskView(state: TaskStateModel): TaskView {
    const taskView = state?.taskView;
    taskView.definition =
      typeof taskView?.definition === 'string' ? JSON.parse(taskView.definition || '{}') : taskView.definition;
    return taskView;
  }

  @Selector()
  static getTaskLists(state: TaskStateModel): TaskList[] {
    return state?.taskLists;
  }

  @Action(TaskActions.CreateTaskList)
  createTaskList(context: StateContext<TaskStateModel>, { taskList }: { taskList: TaskList }) {
    return this.taskListsService.createTaskList(taskList).pipe(
      tap((newTaskList) => {
        context.setState(
          patch({
            taskLists: append([newTaskList]),
          }),
        );
      }),
      switchMap((newTaskList) => {
        if (newTaskList.template_async_population) {
          newTaskList['type'] = 'TaskList';
          const sidebarItems = context.getState().sidebarItems;
          context.patchState({ sidebarItems: sidebarItems.concat([newTaskList as TaskView]) });
          this.taskListsService.taskListAsyncCreation(newTaskList).subscribe(noop);
          return of(newTaskList);
        } else {
          // Dispatch GetSidebarItems and then navigate
          return context.dispatch(new TaskActions.GetSidebarItems()).pipe(
            tap(() => {
              // After sidebar is refreshed, dispatch navigation action
              const viewType = context.getState().viewType || 'list';
              context.dispatch(new TaskActions.NavigateToTaskList(newTaskList.id, viewType));
            }),
          );
        }
      }),
    );
  }

  @Action(TaskActions.NavigateToTaskList)
  navigateToTaskList(
    context: StateContext<TaskStateModel>,
    { taskListId, viewType }: { taskListId: number; viewType?: string },
  ) {
    // This action doesn't change state, it's just a signal for components to listen to
    // Components listening to this action via Actions$ stream will handle the actual navigation
    return of(null);
  }

  @Action(TaskActions.GetTaskListById)
  getTaskListById(
    context: StateContext<TaskStateModel>,
    { taskListId, templateId, includeTasks }: { taskListId: number; templateId: number; includeTasks: any },
  ) {
    return this.taskListsService.getTaskListById(taskListId.toString(), templateId, includeTasks).pipe(
      tap((taskList) => {
        if (includeTasks && taskList.tasks) {
          context.patchState({ tasks: taskList.tasks, taskView: taskList as TaskView });
        } else {
          context.dispatch(new TaskActions.GetTasks(taskList.id, {}));
          context.patchState({ taskView: taskList as TaskView, queryFilters: null });
        }
      }),
    );
  }

  @Action(TaskActions.GetDraftPreviewChanges)
  getDraftPreviewChanges(context: StateContext<TaskStateModel>, { taskListId }: { taskListId: number }) {
    return this.taskListsService.getDraftPreviewChanges(taskListId).pipe(
      tap((response) => {
        context.patchState({ draftPreviewChanges: response.task_changes });
      }),
    );
  }

  @Action(TaskActions.ClearDraftPreviewChanges)
  clearDraftPreviewChanges(context: StateContext<TaskStateModel>) {
    context.patchState({ draftPreviewChanges: [] });
  }

  @Action(TaskActions.PublishDraft)
  publishDraft(context: StateContext<TaskStateModel>, { taskListId }: TaskActions.PublishDraft) {
    return this.taskListsService.publishDraftTaskList(taskListId).pipe(
      tap((response) => {
        context.patchState({
          draftPublishPusherChannel: response.task_list_draft_publish.pusher_channel,
        });
      }),
      catchError((err) => {
        this.snackbar.open('Error publishing draft', 'error', null, { duration: 5000 });
        return of(null);
      }),
    );
  }

  @Action(TaskActions.GetTaskLists)
  getTaskLists(context: StateContext<TaskStateModel>) {
    return this.taskListsService.getTaskLists().pipe(
      tap((taskLists) => {
        context.patchState({ taskLists: taskLists });
      }),
    );
  }

  @Action(TaskActions.DeleteTaskList)
  deleteTaskList(context: StateContext<TaskStateModel>, { taskListId }: { taskListId: number }) {
    return this.taskListsService.deleteTaskList(taskListId).pipe(
      tap(() => {
        context.setState(
          patch({
            taskLists: removeItem<TaskList>((taskList) => taskList.id === taskListId),
            sidebarItems: removeItem<TaskView>((taskList) => taskList.id === taskListId),
          }),
        );
      }),
    );
  }

  @Action(TaskActions.TaskListStartTrackingDates)
  taskListStartTrackingDates(context: StateContext<TaskStateModel>, { taskListId }: { taskListId: number }) {
    return this.taskListsService.startTrackingDates(taskListId).pipe(
      tap(() => {
        context.dispatch(new TaskActions.RecalculateTaskListDates(taskListId));
      }),
    );
  }

  @Action(TaskActions.SetTaskList)
  setTaskList(context: StateContext<TaskStateModel>, { taskList }: { taskList: TaskList }) {
    context.patchState({ taskView: taskList as TaskView });
  }

  @Action(TaskActions.UpdateTaskList)
  updateTaskList(context: StateContext<TaskStateModel>, { taskList }: { taskList: TaskList }) {
    return this.taskListsService.updateTaskList(taskList).pipe(
      tap((taskList) => {
        const taskLists = context.getState().taskLists;
        const sidebarItems = context.getState().sidebarItems;
        const index = taskLists.findIndex((t) => t.id === taskList.id);
        const sidebarItemsIndex = sidebarItems.findIndex((t) => t.id === taskList.id);
        taskLists[index] = taskList;
        sidebarItems[sidebarItemsIndex] = { ...taskList, type: 'TaskList' };
        const state = { sidebarItems: cloneDeep(sidebarItems), taskLists: cloneDeep(taskLists) };
        if (taskList.id === context.getState().taskView.id) {
          state['previousTaskViewVersion'] = taskList;
          state['taskView'] = taskList as TaskView;
        }
        state['taskView'] = { ...state['taskView'], type: 'TaskList' };
        context.patchState(state);
      }),
    );
  }

  @Action(TaskActions.RecalculateTaskListDates, { cancelUncompleted: true })
  recalculateTaskListDates(context: StateContext<TaskStateModel>, { taskListId }: { taskListId: number }) {
    const templateId = context.getState().taskTemplateId;
    return this.taskService.recalculateTaskDates({ id: taskListId }, templateId).pipe(
      mergeMap(() => {
        const state = context.getState();
        if (state.currentTask) {
          context.dispatch(new TaskActions.GetTaskDetail(state.currentTask.task_list_id, state.currentTask.id));
        }

        if (state.queryFilters) {
          return this.redispatchTaskFetch(context);
        } else {
          return context.dispatch(new TaskActions.GetTasks(taskListId, {}));
        }
      }),
    );
  }

  @Action(TaskActions.ClearTaskList)
  clearCurrentTaskList(context: StateContext<TaskStateModel>) {
    context.patchState({
      currentTask: null,
      taskView: null,
      previousTaskViewVersion: null,
      tasks: [],
      queryFilters: null,
      infiniteScroll: undefined,
    });
  }

  // =========================
  // TASKS
  @Selector()
  static getTasks(state: TaskStateModel): Task[] {
    return state?.tasks;
  }

  @Action(TaskActions.GetTasks, { cancelUncompleted: true })
  getTasks(context: StateContext<TaskStateModel>, { taskListId, filters }: { taskListId: number; filters: any }) {
    const templateId = context.getState().taskTemplateId;
    const currentTaskView = context.getState().taskView;
    const taskView = context.getState().taskView;
    let selectFields = taskView.definition?.fields || taskView.definition?.table?.fields || [];
    selectFields = [...new Set([...PRE_FETCH_SELECT_FIELDS, ...selectFields])];
    return this.taskService
      .getTasks(taskListId, templateId, {
        groupBy: currentTaskView.definition?.table?.groupBy || '',
        sort: currentTaskView.definition?.table?.sort || '',
        select: selectFields,
      })
      .pipe(
        tap((tasks) => {
          const transformedTasks = tasks;
          const newState = { tasks: transformedTasks, queryFilters: null };
          const currentTask = context.getState().currentTask;
          if (currentTask) {
            newState['currentTask'] = TaskUtils.deepFindTaskById(transformedTasks, currentTask.id);
          }

          context.patchState(newState);
        }),
      );
  }

  @Action(TaskActions.ReorderColumns, { cancelUncompleted: true })
  reorderColumns(context: StateContext<TaskStateModel>, { columns }: { columns: string[] }) {
    const taskView = context.getState().taskView;
    taskView.definition.fields = columns;
    context.patchState({ taskView });
  }

  @Action(TaskActions.ReorderTasks, { cancelUncompleted: true })
  reorderTasks(
    context: StateContext<TaskStateModel>,
    { taskListId, tasks, tempalteId }: { taskListId: number; tasks: Task[]; tempalteId: number },
  ) {
    return this.taskService.updateTasksOrders({ id: taskListId }, tasks, tempalteId).pipe(
      tap(() => {
        const stateTasks = context.getState().tasks;
        // deep update task rank
        const updateTaskRank = (stateTasks, tasks) => {
          return stateTasks.map((task) => {
            const newTask = tasks.find((t) => t.id === task.id);
            const updatedTask = {
              ...task,
              rank: (newTask || task)?.rank,
            };
            if (updatedTask.children) {
              updatedTask.children = updateTaskRank(updatedTask.children, tasks);
            }
            return updatedTask;
          });
        };

        const newTasksState = updateTaskRank(stateTasks, tasks);
        const sortedTasks = TaskUtils.deepSortTasksByRank(newTasksState);
        context.patchState({ tasks: sortedTasks });
      }),
    );
  }

  @Action(TaskActions.ReorderTaskSection, { cancelUncompleted: true })
  reorderTaskSection(
    context: StateContext<TaskStateModel>,
    { taskListId, updateData, optimisticTasks }: TaskActions.ReorderTaskSection,
  ) {
    const previousTasks = context.getState().tasks;
    if (optimisticTasks) {
      context.patchState({ tasks: optimisticTasks });
    }
    return this.taskService.reorderTaskSection(taskListId, updateData as Task).pipe(
      tap(() => {
        // background ranks resync
        const queryFilters = context.getState().queryFilters;
        if (queryFilters) {
          context.dispatch(new TaskActions.SyncCascadingChanges(queryFilters));
        }
      }),
      catchError(() => {
        context.patchState({ tasks: previousTasks });
        this.snackbar.open('Failed to reorder task. Please try again.', 'error', null, { duration: 5000 });
        return of(null);
      }),
    );
  }

  // deletaaction
  @Action(TaskActions.DeleteTask)
  deleteTask(
    context: StateContext<TaskStateModel>,
    { taskListId, taskId, templateId }: { taskListId: number; taskId: number; templateId: number },
  ) {
    return this.taskService.deleteTask(taskListId, templateId, taskId).pipe(
      tap(() => {
        const currentTask = cloneDeep(context.getState().currentTask);
        let deletingCurrentTaskChild = false;

        const filterTask = (arr, id) => {
          return arr.filter((task, index) => {
            if (task.id === id) {
              deletingCurrentTaskChild = task.parent_id === currentTask?.id;
              return false;
            }
            if (task.children) {
              task.children = filterTask(task.children, id);
            }
            return true;
          });
        };

        const state = context.getState();
        const newTasksArray = filterTask(state.tasks, taskId);
        let newState: Partial<TaskStateModel> = { tasks: newTasksArray };

        if (deletingCurrentTaskChild) {
          currentTask.children = filterTask(currentTask?.children, taskId);
          newState['currentTask'] = currentTask;
        }

        context.patchState(newState);
        context.dispatch(new TaskActions.InvalidatePredecessorOptions());

        const queryFilters = context.getState().queryFilters;
        if (context.getState().taskView && queryFilters) {
          context.dispatch(new TaskActions.SyncCascadingChanges(queryFilters));
        }
      }),
      this.updateCount(context),
    );
  }

  private executeSearch(filter: TaskQueryFilter): Observable<{ tasks: Task[]; totalItems: number }> {
    return this.taskService.search(filter).pipe(
      rxFilter((response) => !response.data?.message),
      map((response) => ({ tasks: response.tasks ?? [], totalItems: +response.count })),
    );
  }

  private buildSearchFilter(
    context: StateContext<TaskStateModel>,
    queryFilters: TaskQueryFilter,
  ): Record<string, unknown> {
    const moveTitleToFirst = (arr: string[]) => {
      const filtered = arr.filter((item) => item !== 'title');
      filtered.unshift('title');
      return filtered;
    };

    const filters = { ...queryFilters };
    filters['nested_children'] = context.getState().viewType === TaskViewType.Gantt;
    filters['aggregated_tasks'] = context.getState().relatedToType === TaskRelatedToModule.AggregatedTasks;

    const taskView: TaskView = cloneDeep(context.getState().taskView);
    const tempFilter = queryFilters.fjson ? JSON.parse(queryFilters.fjson) : [];
    const group = queryFilters.group;

    const pagination = context.getState().pagination;
    if (pagination) {
      filters['pagination'] = {
        page: pagination.page,
        pageSize: pagination.pageSize,
      };
    }

    if (
      !filters['aggregated_tasks'] &&
      taskView?.id &&
      taskView.type !== 'SavedView' &&
      !tempFilter.some((f: unknown[]) => f[0] === 'task_list_id')
    ) {
      tempFilter.unshift(['task_list_id', 'eq', taskView.id]);
    }

    const perPage =
      +((filters['pagination'] as { pageSize?: number })?.pageSize ?? context.getState().pagination?.pageSize) || 500;

    let selectFields = taskView.definition?.fields || taskView.definition?.table?.fields || [];

    // [59538] | This means we are exporting tasks, so no need to include the pre-fetch fields to avoid outputting unnecessary columns.
    if (queryFilters.format) {
      selectFields = [...new Set([...selectFields])];
    } else {
      selectFields = [...new Set([...PRE_FETCH_SELECT_FIELDS, ...selectFields])];
    }

    selectFields = moveTitleToFirst(selectFields);

    const filter: Record<string, unknown> = {
      ...filters,
      fjson: JSON.stringify(tempFilter),
      sort: queryFilters.sort || [],
      q: queryFilters.q,
      allow_templates: this.isTaskListTemplate,
      page: (filters['pagination'] as { page?: number })?.page || 1,
      per_page: perPage,
      format: queryFilters.format ?? undefined,
      nested_children: filters['nested_children'],
      aggregated_tasks: filters['aggregated_tasks'],
      select: selectFields.join(','),
      group,
      task_list_id: taskView.type !== 'SavedView' ? taskView?.id : undefined,
    };

    delete filter.pagination;

    return filter;
  }

  /**
   * Routes task re-fetch through infinite scroll or pagination depending on active mode.
   * Used after CRUD operations that invalidate the task list.
   */
  private redispatchTaskFetch(context: StateContext<TaskStateModel>, showProgressBar = false) {
    const state = context.getState();
    const scroll = state.infiniteScroll;
    let queryFilters = state.queryFilters;

    if (scroll) {
      queryFilters = {
        ...queryFilters,
        pagination: { page: 1, pageSize: scroll.currentPage * scroll.pageSize },
      };
    }

    return context.dispatch(new TaskActions.FilterTasks(queryFilters || {}, showProgressBar));
  }

  @Action(TaskActions.FilterTasks)
  filterTasks(
    context: StateContext<TaskStateModel>,
    params: { queryFilters: TaskQueryFilter; showProgressBar?: boolean },
  ) {
    const queryFilters = (params.queryFilters as TaskQueryFilter) || {};
    const isInfiniteScroll = this.isInfiniteScrollMode;
    const filter = this.buildSearchFilter(context, queryFilters) as TaskQueryFilter;

    if (isInfiniteScroll) {
      filter.page = 1;
      filter.per_page = INFINITE_SCROLL_INITIAL_PAGE_SIZE;
    }

    return this.executeSearch(filter).pipe(
      tap(({ tasks, totalItems }) => {
        if (isInfiniteScroll) {
          const dedupedTasks = TaskUtils.mergeTaskPages([], tasks);
          const realTaskCount = dedupedTasks.filter((t) => t.task_type !== 'section').length;
          const hasMore = realTaskCount < totalItems;
          context.patchState({
            queryFilters: filter,
            tasks: dedupedTasks,
            infiniteScroll: {
              currentPage: INFINITE_SCROLL_INITIAL_PAGES,
              totalItems,
              isLoadingMore: false,
              hasMore,
              pageSize: INFINITE_SCROLL_PAGE_SIZE,
            },
          });
          if (hasMore) {
            context.dispatch(new TaskActions.LoadMoreTasks());
          }
        } else {
          const pagination = context.getState().pagination;
          context.patchState({
            queryFilters: filter,
            tasks,
            pagination: {
              ...pagination,
              pageSize: filter.per_page!,
              page: filter.page!,
              totalItems,
            },
          });
        }
      }),
    );
  }

  @Action(TaskActions.LoadMoreTasks)
  loadMoreTasks(context: StateContext<TaskStateModel>, { source }: TaskActions.LoadMoreTasks) {
    const state = context.getState();
    const scroll = state.infiniteScroll;
    if (!scroll || !scroll.hasMore || scroll.isLoadingMore) return EMPTY;

    if (source === 'scroll') {
      this.dataDogService.logAction('Tasks - Infinite Scroll Load More');
    }

    context.patchState({ infiniteScroll: { ...scroll, isLoadingMore: true } });

    const nextPage = scroll.currentPage + 1;
    const filter = { ...state.queryFilters, page: nextPage, per_page: scroll.pageSize } as TaskQueryFilter;
    const isNested = state.viewType === TaskViewType.Gantt;

    return this.executeSearch(filter).pipe(
      tap(({ tasks: newTasks, totalItems }) => {
        const currentTasks = context.getState().tasks;
        const merged = isNested
          ? TaskUtils.mergeTaskPagesNested(currentTasks, newTasks)
          : TaskUtils.mergeTaskPages(currentTasks, newTasks);
        const realTaskCount = merged.filter((t) => t.task_type !== 'section').length;

        context.patchState({
          tasks: merged,
          infiniteScroll: {
            ...context.getState().infiniteScroll!,
            currentPage: nextPage,
            totalItems,
            isLoadingMore: false,
            hasMore: realTaskCount < totalItems,
          },
        });
      }),
      catchError(() => {
        context.patchState({
          infiniteScroll: { ...context.getState().infiniteScroll!, isLoadingMore: false },
        });
        return of(null);
      }),
    );
  }

  @Action(TaskActions.ResetInfiniteScroll)
  resetInfiniteScroll(context: StateContext<TaskStateModel>) {
    context.patchState({ infiniteScroll: undefined, tasks: [] });
  }

  @Action(TaskActions.FetchPartialTaskData)
  fetchPartialTaskData(
    context: StateContext<TaskStateModel>,
    { fields }: TaskActions.FetchPartialTaskData,
  ) {
    const state = context.getState();
    if (!fields?.length) return EMPTY;

    const scroll = state.infiniteScroll;
    const isNested = state.viewType === TaskViewType.Gantt;

    const base = this.buildSearchFilter(context, state.queryFilters || {}) as TaskQueryFilter;
    let page: number;
    let perPage: number;
    if (scroll) {
      page = 1;
      perPage = scroll.currentPage * scroll.pageSize;
    } else {
      const pagination = state.pagination;
      page = pagination?.page ?? 1;
      perPage = pagination?.pageSize ?? state.tasks.length;
    }

    const filter: TaskQueryFilter = {
      ...base,
      page,
      per_page: perPage,
      select: ['id', ...fields].join(','),
      partial_fetch: true,
      nested_children: isNested,
    };

    return this.executeSearch(filter).pipe(
      tap(({ tasks: partial }) => {
        const merged = TaskUtils.mergePartialTaskFields(
          context.getState().tasks,
          partial,
          fields,
          isNested,
        );
        context.patchState({ tasks: merged });
      }),
      catchError(() => {
        this.snackbar.open('Failed to load new column data', 'error', undefined, {
          duration: 5000,
        });
        return of(null);
      }),
    );
  }

  @Action(TaskActions.AutoLoadAllTasks)
  autoLoadAllTasks(ctx: StateContext<TaskStateModel>) {
    return of(null).pipe(
      expand(() => {
        const scroll = ctx.getState().infiniteScroll; 
        if (!scroll?.hasMore) return EMPTY;
        return ctx.dispatch(new TaskActions.LoadMoreTasks());
      }),
    );
  }

  @Action(TaskActions.SyncCascadingChanges)
  syncCascadingChanges(ctx: StateContext<TaskStateModel>, { queryFilters }: TaskActions.SyncCascadingChanges) {
    const state = ctx.getState();
    const scroll = state.infiniteScroll;

    if (scroll) {
      // Infinite scroll: fetch all loaded pages to get a complete diff
      const filter = this.buildSearchFilter(ctx, queryFilters) as TaskQueryFilter;
      filter.page = 1;
      filter.per_page = scroll.currentPage * scroll.pageSize;

      return this.taskService.search(filter).pipe(
        tap((response) => {
          if (response.data?.message) return;
          const apiTasks = response.tasks ?? [];
          const visibleFields = state.taskView?.definition?.fields || state.taskView?.definition?.table?.fields || [];
          this.pretendTaskChangePusher.diffAndEmit(apiTasks, state.tasks, visibleFields);

          const dedupedTasks = TaskUtils.mergeTaskPages([], apiTasks);
          const totalItems = +response.count;
          const realTaskCount = dedupedTasks.filter((t) => t.task_type !== 'section').length;

          ctx.patchState({
            queryFilters: filter,
            tasks: dedupedTasks,
            infiniteScroll: {
              ...scroll,
              totalItems,
              hasMore: realTaskCount < totalItems,
            },
          });
        }),
      );
    }

    // Original pagination mode — unchanged
    const filter = this.buildSearchFilter(ctx, queryFilters) as TaskQueryFilter;
    return this.taskService.search(filter).pipe(
      tap((response) => {
        if (response.data?.message) return;
        const currentTasks = state.tasks;
        const apiTasks = response.tasks ?? [];
        const visibleFields = state.taskView?.definition?.fields || state.taskView?.definition?.table?.fields || [];
        this.pretendTaskChangePusher.diffAndEmit(apiTasks, currentTasks, visibleFields);

        ctx.patchState({
          queryFilters: filter,
          tasks: apiTasks,
          pagination: {
            ...state.pagination,
            pageSize: filter.per_page,
            page: filter.page,
            totalItems: +response.count,
          },
        });
      }),
    );
  }

  @Action(TaskActions.InvalidatePredecessorOptions)
  invalidatePredecessorOptions(context: StateContext<TaskStateModel>) {
    context.patchState({ predecessorOptionsDirty: true });
  }

  @Action(TaskActions.LoadTaskPredecessorOptions, { cancelUncompleted: true })
  loadTaskPredecessorOptions(context: StateContext<TaskStateModel>) {
    const state = context.getState();
    const currentTask = state.currentTask;
    const taskView = state.taskView;
    const selectFields = ['id', 'title', 'task_type', 'task_list_id'];
    const baseFilter: TaskQueryFilter = state.queryFilters ? cloneDeep(state.queryFilters) : {};
    const taskListId = taskView?.type !== 'SavedView' ? taskView?.id : currentTask?.task_list_id;

    if (!taskListId) {
      return of(null);
    }

    if (!state.predecessorOptionsDirty && (state.taskPredecessorOptions?.length ?? 0) > 0) {
      return of(null);
    }

    const taskListFilter = [['task_list_id', 'eq', taskListId]];
    const optionsFilter: TaskQueryFilter = {
      ...baseFilter,
      fjson: JSON.stringify(taskListFilter),
      select: selectFields.join(','),
      format: undefined,
      page: 1,
      per_page: 10000,
    };

    return this.taskService.search(optionsFilter).pipe(
      tap((response) => {
        const tasks = TaskUtils.flattenTasks(response?.tasks || []);
        const scopedToTaskList = tasks.filter((t) => t?.task_list_id === taskListId);

        const isKeyDateLinkingEnabled = this.store.selectSnapshot(
          AccountState.isFeatureEnabled(FeatureFlag.KEY_DATE_LINKING),
        );
        const isGlobalTask =
          state.relatedToType === TaskRelatedToModule.TaskListTemplate
            ? (taskView as any)?.applicable_type === RelatedToType.Account
            : state.relatedToType === TaskRelatedToModule.Account;

        const allowedTypes: TaskType[] = isKeyDateLinkingEnabled && !isGlobalTask ? ['task', 'key_date'] : ['task'];

        const uniqueById = scopedToTaskList.filter(
          (task, index, self) => self.findIndex((t) => t.id === task.id) === index,
        );
        const filtered = uniqueById.filter((task) => allowedTypes.includes(task.task_type as TaskType));

        context.patchState({
          taskPredecessorOptions: filtered,
          predecessorOptionsDirty: false,
        });
      }),
    );
  }

  @Action(TaskActions.TaskList.ToggleVisibility)
  toggleVisibility(context: StateContext<TaskStateModel>, { id }: { id: number }) {
    const taskList = context.getState().sidebarItems.find((t) => t.id === id);

    taskList.visibility = taskList.visibility === 'private_visibility' ? 'public_visibility' : 'private_visibility';

    return this.taskListsService.updateTaskList(taskList).pipe(
      tap(() => {
        const taskLists = context.getState().sidebarItems;
        const index = taskLists.findIndex((t) => t.id === taskList.id);
        taskLists[index] = taskList;
        context.patchState({ sidebarItems: [...taskLists] });
      }),
    );
  }

  @Action(TaskActions.UpdateCurrentTaskNums)
  updateCurrentTaskNums(context: StateContext<TaskStateModel>) {
    const currentTask = context.getState().currentTask;
    return this.taskService
      .getTaskDetailById(currentTask.task_list_id, currentTask.id, ['num_comments', 'last_comment'])
      .pipe(
        tap((task) => {
          const tasks = TaskUtils.deepUpdateTaskById(cloneDeep(context.getState().tasks), task);
          context.patchState({ currentTask: { ...currentTask, ...task }, tasks });
        }),
      );
  }

  private updateCount(context: StateContext<TaskStateModel>): (source$: Observable<any>) => Observable<any> {
    return (source$) => {
      return source$.pipe(
        rxFilter(
          () =>
            !context.getState().taskView?.is_draft &&
            ![RelatedToType.AggregatedTasks, RelatedToType.Contact, RelatedToType.Company].includes(
              context.getState().taskView?.related_to_type as RelatedToType,
            ),
        ),
        switchMap(() =>
          this.taskListsService.getTaskListByIdAndSelect<Pick<TaskList, 'task_count' | 'complete_task_count'>>(
            context.getState().taskView?.id,
            ['task_count', 'complete_task_count'],
          ),
        ),
        tap((taskList: TaskList) => {
          const sidebarItems = context.getState().sidebarItems;
          const currentView = context.getState().taskView;
          const taskView = sidebarItems.findIndex((item) => item.id === currentView.id);

          sidebarItems[taskView].task_count = taskList.task_count;

          const taskCounters = { ...context.getState().taskCounters };

          taskCounters[taskList.id] = {
            currentTaskCount: taskList.task_count,
            currentTaskCompleteCount: taskList.complete_task_count,
          };

          context.patchState({
            taskCounters,
            sidebarItems: cloneDeep(sidebarItems),
          });
        }),
      );
    };
  }
}
